import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

import Header from '../../components/ui/Header';
import QRScanner from './components/QRScanner';
import ValidationResult from './components/ValidationResult';
import ScanHistory from './components/ScanHistory';
import ManualCodeEntry from './components/ManualCodeEntry';

const QRCodeScannerValidator = () => {
  const { user, currentTenant, isGymOwner, isStaff, isTrainer, profileLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('scanner');
  const [validationResult, setValidationResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    todayScans: 0,
    successfulScans: 0,
    failedScans: 0,
    uniqueMembers: 0
  });

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    if (profileLoading) return;
    if (!isStaffMember) return;
    if (currentTenant?.id) {
      fetchScanHistory();
      fetchStats();
    }
  }, [currentTenant?.id, isStaffMember, profileLoading]);

  const fetchScanHistory = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        ?.from('attendance_logs')
        ?.select(`
          *,
          user_profiles!attendance_logs_user_id_fkey(full_name, email, avatar_url),
          member_barcodes!attendance_logs_barcode_id_fkey(barcode_data, barcode_type)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('check_in_time', today?.toISOString())
        ?.order('check_in_time', { ascending: false })
        ?.limit(50);

      if (error) throw error;
      setScanHistory(data || []);
    } catch (error) {
      console.error('Error fetching scan history:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        ?.from('attendance_logs')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('check_in_time', today?.toISOString());

      if (error) throw error;

      const uniqueMembers = new Set(data?.map(log => log?.user_id))?.size;

      setStats({
        todayScans: data?.length || 0,
        successfulScans: data?.filter(log => log?.entry_type === 'barcode_scan')?.length || 0,
        failedScans: 0, // Can be tracked separately if needed
        uniqueMembers
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleQRCodeScanned = async (qrData) => {
    setLoading(true);
    setValidationResult(null);

    try {
      // Find QR code in database
      const { data: qrRecord, error: qrError } = await supabase
        ?.from('member_barcodes')
        ?.select(`
          *,
          user_profiles!member_barcodes_user_id_fkey(id, full_name, email, avatar_url, phone),
          tenant_members!inner(role, membership_plan, is_active, membership_status)
        `)
        ?.eq('barcode_data', qrData)
        ?.eq('tenant_id', currentTenant?.id)
        ?.single();

      if (qrError || !qrRecord) {
        setValidationResult({
          success: false,
          message: 'Invalid QR code. Not found in system.',
          type: 'not_found'
        });
        return;
      }

      // Check if QR code is active
      if (!qrRecord?.is_active) {
        setValidationResult({
          success: false,
          message: 'QR code has been deactivated.',
          type: 'inactive',
          member: qrRecord?.user_profiles
        });
        return;
      }

      // Check expiration
      if (qrRecord?.expires_at && new Date(qrRecord?.expires_at) <= new Date()) {
        setValidationResult({
          success: false,
          message: 'QR code has expired.',
          type: 'expired',
          member: qrRecord?.user_profiles,
          expiresAt: qrRecord?.expires_at
        });
        return;
      }

      // Check member status
      if (!qrRecord?.tenant_members?.[0]?.is_active) {
        setValidationResult({
          success: false,
          message: 'Member account is inactive. Please contact reception.',
          type: 'member_inactive',
          member: qrRecord?.user_profiles
        });
        return;
      }

      // Check for existing check-in
      const { data: existingCheckIn } = await supabase
        ?.from('attendance_logs')
        ?.select('*')
        ?.eq('user_id', qrRecord?.user_profiles?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.is('check_out_time', null)
        ?.order('check_in_time', { ascending: false })
        ?.limit(1)
        ?.single();

      if (existingCheckIn) {
        // Check out
        await supabase
          ?.from('attendance_logs')
          ?.update({ check_out_time: new Date()?.toISOString() })
          ?.eq('id', existingCheckIn?.id);

        setValidationResult({
          success: true,
          message: 'Member checked out successfully!',
          type: 'check_out',
          member: qrRecord?.user_profiles,
          membershipPlan: qrRecord?.tenant_members?.[0]?.membership_plan,
          checkInTime: existingCheckIn?.check_in_time
        });
      } else {
        // Check in
        await supabase
          ?.from('attendance_logs')
          ?.insert({
            tenant_id: currentTenant?.id,
            user_id: qrRecord?.user_profiles?.id,
            barcode_id: qrRecord?.id,
            check_in_time: new Date()?.toISOString(),
            entry_type: 'barcode_scan'
          });

        setValidationResult({
          success: true,
          message: 'Member checked in successfully!',
          type: 'check_in',
          member: qrRecord?.user_profiles,
          membershipPlan: qrRecord?.tenant_members?.[0]?.membership_plan
        });
      }

      // Update scan count
      await supabase
        ?.from('member_barcodes')
        ?.update({
          scan_count: (qrRecord?.scan_count || 0) + 1,
          last_scanned_at: new Date()?.toISOString()
        })
        ?.eq('id', qrRecord?.id);

      // Refresh data
      fetchScanHistory();
      fetchStats();
    } catch (error) {
      console.error('Error validating QR code:', error);
      setValidationResult({
        success: false,
        message: 'Error processing QR code. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isStaffMember) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-card rounded-lg p-8 text-center">
            <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              This feature is only available to gym staff, trainers, and owners.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            QR Code Scanner & Validator
          </h1>
          <p className="text-muted-foreground">
            Scan and validate member QR codes for access control and attendance tracking
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Today's Scans</span>
              <Icon name="Scan" size={20} className="text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {stats?.todayScans}
            </p>
          </div>

          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Successful</span>
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {stats?.successfulScans}
            </p>
          </div>

          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Failed</span>
              <Icon name="XCircle" size={20} className="text-destructive" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {stats?.failedScans}
            </p>
          </div>

          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Unique Members</span>
              <Icon name="Users" size={20} className="text-info" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {stats?.uniqueMembers}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('scanner')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'scanner' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Camera" size={18} className="inline mr-2" />
            QR Scanner
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'manual' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Keyboard" size={18} className="inline mr-2" />
            Manual Entry
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'history' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="History" size={18} className="inline mr-2" />
            Scan History
          </button>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {activeTab === 'scanner' && (
            <>
              <QRScanner
                onScan={handleQRCodeScanned}
                loading={loading}
              />
              <ValidationResult
                result={validationResult}
                loading={loading}
              />
            </>
          )}

          {activeTab === 'manual' && (
            <>
              <ManualCodeEntry
                onSubmit={handleQRCodeScanned}
                loading={loading}
              />
              <ValidationResult
                result={validationResult}
                loading={loading}
              />
            </>
          )}
        </div>

        {activeTab === 'history' && (
          <ScanHistory
            history={scanHistory}
            onRefresh={fetchScanHistory}
          />
        )}
      </div>
    </div>
  );
};

export default QRCodeScannerValidator;